import type { Dayjs } from 'dayjs';
import { tKey } from 'ember-intl';
import { SDLY, SWLY } from 'lumen/utils/reference-period';

export const ASK_ERNEST_PAGES = {
  BOOKINGS: 'bi-bookings',
  EVENTS: 'events',
  MARKET: 'market',
  PARITY: 'parity',
  RANKING: 'ranking',
  RATES: 'rates',
} as const;

export type AskErnestPage =
  (typeof ASK_ERNEST_PAGES)[keyof typeof ASK_ERNEST_PAGES];

// The page's comparison selector also carries values Ernest cannot read, such as
// a custom period, so anything else falls back to SWLY, the page default.
export const ASK_ERNEST_COMPARISONS = [SDLY, SWLY];
export const DEFAULT_ASK_ERNEST_COMPARISON = SWLY;

export type AskErnestPeriod = { endDate: Dayjs; startDate: Dayjs };

export type AskErnestPrompt = {
  id: string;
  label: string;
  message: string;
  // The stay dates the prompt asks about, instead of the dates on the page
  period?: (today: Dayjs) => AskErnestPeriod;
};

// Shared by the tabs whose prompt asks what needs attention, so rewording it
// changes Bookings, Events, Parity and Ranking together.
const NEEDS_ATTENTION_LABEL = tKey(
  'general.ask_ernest.prompts.needs_attention',
);

export const BOOKINGS_PROMPTS: AskErnestPrompt[] = [
  {
    id: 'needs-attention',
    label: NEEDS_ATTENTION_LABEL,
    message: tKey('general.ask_ernest.messages.bookings_attention'),
  },
  {
    id: 'one-day-pickup',
    label: tKey('general.ask_ernest.prompts.one_day_pickup'),
    message: tKey('general.ask_ernest.messages.one_day_pickup'),
  },
  {
    id: 'segment-drilldown',
    label: tKey('general.ask_ernest.prompts.segment_drilldown'),
    message: tKey('general.ask_ernest.messages.segment_drilldown'),
  },
];

// The weekend is the Friday and Saturday stay dates of the current week, as on
// the Rates chart, without the days that have already passed. On a Sunday it is
// the next weekend.
function thisWeekend(today: Dayjs): AskErnestPeriod {
  const week = today.isoWeekday() === 7 ? today.add(1, 'day') : today;
  const friday = week.isoWeekday(5);

  return {
    endDate: week.isoWeekday(6),
    startDate: friday.isBefore(today, 'day') ? today : friday,
  };
}

export const RATES_PROMPTS: AskErnestPrompt[] = [
  {
    id: 'weekend-rate-changes',
    label: tKey('general.ask_ernest.prompts.weekend_rate_changes'),
    message: tKey('general.ask_ernest.messages.weekend_rate_changes'),
    period: thisWeekend,
  },
  {
    id: 'dates-need-attention',
    label: tKey('general.ask_ernest.prompts.dates_need_attention'),
    message: tKey('general.ask_ernest.messages.dates_need_attention'),
    period: (today): AskErnestPeriod => ({
      endDate: today.add(90, 'day'),
      startDate: today.add(30, 'day'),
    }),
  },
  {
    id: 'event-date-rates',
    label: tKey('general.ask_ernest.prompts.event_date_rates'),
    message: tKey('general.ask_ernest.messages.event_date_rates'),
    period: (today): AskErnestPeriod => {
      const nextMonth = today.add(1, 'month');

      return {
        endDate: nextMonth.endOf('month'),
        startDate: nextMonth.startOf('month'),
      };
    },
  },
];

export const EVENTS_PROMPTS: AskErnestPrompt[] = [
  {
    id: 'upcoming-events',
    label: tKey('general.ask_ernest.prompts.upcoming_events'),
    message: tKey('general.ask_ernest.messages.upcoming_events'),
  },
  {
    id: 'events-attention',
    label: NEEDS_ATTENTION_LABEL,
    message: tKey('general.ask_ernest.messages.events_attention'),
  },
];

export const MARKET_PROMPTS: AskErnestPrompt[] = [
  {
    id: 'highest-demand',
    label: tKey('general.ask_ernest.prompts.highest_demand'),
    message: tKey('general.ask_ernest.messages.highest_demand'),
  },
  {
    id: 'search-countries',
    label: tKey('general.ask_ernest.prompts.search_countries'),
    message: tKey('general.ask_ernest.messages.search_countries'),
  },
  {
    id: 'strong-demand-flat-rates',
    label: tKey('general.ask_ernest.prompts.strong_demand_flat_rates'),
    message: tKey('general.ask_ernest.messages.strong_demand_flat_rates'),
  },
];

export const PARITY_PROMPTS: AskErnestPrompt[] = [
  {
    id: 'parity-attention',
    label: NEEDS_ATTENTION_LABEL,
    message: tKey('general.ask_ernest.messages.parity_attention'),
  },
];

export const RANKING_PROMPTS: AskErnestPrompt[] = [
  {
    id: 'ota-ranking',
    label: tKey('general.ask_ernest.prompts.ota_ranking'),
    message: tKey('general.ask_ernest.messages.ota_ranking'),
  },
  {
    id: 'ranking-attention',
    label: NEEDS_ATTENTION_LABEL,
    message: tKey('general.ask_ernest.messages.ranking_attention'),
  },
];
